import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import DataUsageIcon from '@mui/icons-material/DataUsage';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import copy from 'copy-to-clipboard';
import {bool, object} from 'prop-types';
import React, {useCallback, useState} from 'react';
import {useDispatch} from 'react-redux';
import DeleteCollectionModal from './DeleteCollectionModal.jsx';
import EditCollectionModal from './EditCollectionModal.jsx';
import ShareCollectionModal from './ShareCollectionModal.jsx';
import {notify} from '@actions/notifications';
import {trackEvent} from '@app/analytics';
import {MODALS} from '@app/constants';
import useCollectionParams from '@app/hooks/useCollectionParams';
import QuotaManagerModal from '@components/QuotaManager/Modal';
import ActionsMenuButton from '@components/common/ActionsMenuButton';
import MenuItem from '@components/common/MenuItem';

const EVENT_NAME = 'Collection Actions Menu';

const MoreActionsMenu = ({collection, hasProducts, ...props}) => {
  const dispatch = useDispatch();
  const {collectionId: activeCollectionId} = useCollectionParams();
  const hasNoFeatures = collection.feature_count === 0;

  const [modal, setModal] = useState('');

  const showQuotaMenu = hasProducts;

  const closeModal = useCallback(() => {
    setModal('');
  }, [setModal]);

  const onClick = () => {
    trackEvent(EVENT_NAME, {
      action: 'opened',
      collectionId: collection.id,
      featureCount: collection.feature_count,
    });
  };

  const onCopyLink = () => {
    const url = new URL(window.location.href);

    if (!activeCollectionId) {
      url.searchParams.set('collection', collection.id);
    }

    const prettyURL = `${url.origin}${url.pathname}?${decodeURIComponent(url.searchParams.toString())}`;

    copy(prettyURL);

    dispatch(
      notify({
        message: 'Copied',
      })
    );

    trackEvent(EVENT_NAME, {
      action: 'copied link',
      collectionId: collection.id,
      featureCount: collection.feature_count,
      link: prettyURL,
    });
  };
  return (
    <>
      <ActionsMenuButton tooltipTitle="More" onClick={onClick} {...props}>
        <MenuItem
          key="edit"
          label="Edit"
          icon={<EditOutlinedIcon />}
          onClick={() => {
            trackEvent(EVENT_NAME, {
              action: 'Opened edit modal',
              collectionId: collection.id,
              featureCount: collection.feature_count,
            });
            setModal(MODALS.EDIT_COLLECTION);
          }}
          disabled={!collection?.permissions?.is_owner}
        />

        <MenuItem
          key="copyLink"
          label="Copy link"
          icon={<ContentCopyOutlinedIcon />}
          onClick={onCopyLink}
          divider
        />
        <MenuItem
          key="share"
          label="Manage sharing"
          icon={<GroupOutlinedIcon />}
          onClick={() => {
            trackEvent(EVENT_NAME, {
              action: 'Opened manage sharing modal',
              collectionId: collection.id,
              featureCount: collection.feature_count,
            });
            setModal(MODALS.SHARE_COLLECTION);
          }}
          disabled={!collection?.permissions?.is_owner}
          divider={!showQuotaMenu}
        />
        {showQuotaMenu && (
          <MenuItem
            key="quotaMenu"
            label="Manage quota"
            icon={<DataUsageIcon />}
            onClick={() => {
              trackEvent(EVENT_NAME, {
                action: `Opened manage quota modal`,
                collectionId: collection.id,
                featureCount: collection.feature_count,
              });
              setModal(MODALS.QUOTA_MANAGER);
            }}
            disabled={hasNoFeatures}
            divider
          />
        )}
        <MenuItem
          key="delete"
          label="Delete"
          icon={<DeleteOutlinedIcon />}
          color="error"
          onClick={() => {
            trackEvent(EVENT_NAME, {
              action: 'Opened delete modal',
              collectionId: collection.id,
              featureCount: collection.feature_count,
            });
            setModal(MODALS.DELETE_COLLECTION);
          }}
          disabled={!collection?.permissions?.is_owner || collection?.locked}
          tooltipText={
            collection?.locked
              ? 'Cannot be deleted because quota is in use'
              : ''
          }
        />
      </ActionsMenuButton>
      {collection && (
        <>
          <EditCollectionModal
            open={modal === MODALS.EDIT_COLLECTION}
            collection={collection}
            onClose={closeModal}
          />
          <DeleteCollectionModal
            open={modal === MODALS.DELETE_COLLECTION}
            collection={collection}
            onClose={closeModal}
          />
          <ShareCollectionModal
            open={modal === MODALS.SHARE_COLLECTION}
            collection={collection}
            onClose={closeModal}
          />
        </>
      )}
      {showQuotaMenu && (
        <QuotaManagerModal
          open={modal === MODALS.QUOTA_MANAGER}
          onClose={closeModal}
          collection={collection}
        />
      )}
    </>
  );
};

MoreActionsMenu.propTypes = {
  collection: object.isRequired,
  hasProducts: bool,
};

export default MoreActionsMenu;
